// supervisor-release-flow-resume.ts
//
// Bug G (P14 real-release-flow dogfood): the release flow was built and
// E2E-proven at P7 but NEVER called from `avatar autopilot run`. This module
// is that caller: `resumeReleaseFlowIfConfigured` runs ONCE after
// runSupervisorUntilIdle's "done", reads gates.json to see which step the
// mission is on (idempotent — safe on every `run`, including after an
// `approve g3` from a SEPARATE process), and drives the next step. No `config.release` => no-op.
//
// H14 bản 2 (2026-09-29):
//   - the G3 approval is bound to the release MANIFEST (candidate + recipes +
//     release params, release-manifest-hash.ts); same candidate, different
//     manifest ⇒ a fresh G3 request BEFORE any side effect (B2-1);
//   - deploy without verify is refused before G3 is ever asked (D14);
//   - the approved leg (supervisor-release-approved-g3-leg.ts) never runs a
//     stand-in recipe and never self-matches rollout.
import type { MissionStore } from "./mission-store.js";
import { pushDestinationFor } from "./recorded-draft-mr-push.js";
import { verifyCiOfApprovedCandidate } from "./release-ci-gate-approved-candidate.js";
import {
  G3_CONCURRENT_WRITE_REASON,
  readG3WithdrawingOtherCandidate,
  reRequestG3IfStale,
  withG3WithdrawalNote,
} from "./release-g3-rerequest.js";
import { computeReleaseManifestHash } from "./release-manifest-hash.js";
import {
  candidateShaReader,
  precheckDeployStateBeforeRelease,
} from "./release-resume-deploy-state-precheck.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";
import { runApprovedG3ReleaseLeg } from "./supervisor-release-approved-g3-leg.js";
import { approveG3 as approveG3Gate } from "./supervisor-release-deploy-verify.js";
import type { ResumeReleaseFlowInput } from "./supervisor-release-flow-resume-input.js";
import type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";
import { resumeUpToG3 } from "./supervisor-release-flow-resume-up-to-g3.js";

export type { ResumeReleaseFlowInput } from "./supervisor-release-flow-resume-input.js";
export type { ReleaseFlowResumeOutcome } from "./supervisor-release-flow-resume-outcome.js";

export const DEPLOY_WITHOUT_VERIFY_REASON =
  "config.release có deploy_recipe mà thiếu verify_recipe — không deploy khi không có smoke kiểm " +
  "lại (§14 'chỉ DONE khi đủ evidence'); thêm verify_recipe rồi chạy lại.";

/**
 * Runs once after all tickets are "done". Idempotent per gate state:
 *   legacy receipt or unknown deploy (any g3 status) -> stop, gate untouched (M1)
 *   g3 not_requested/waived/invalidated -> runReleaseFlowUpToG3 (review -> push -> remote evidence -> request G3)
 *   g3 pending/approved, other candidate -> invalidated, then as not_requested (CXR-03)
 *   g3 pending/approved, manifest changed, no push to the current destination -> same (CXR-R2)
 *   g3 pending/approved, manifest changed -> fresh G3 request (0 side effects)
 *   g3 pending        -> still waiting on a human `approve g3`
 *   g3 approved       -> runApprovedG3ReleaseLeg (deploy/smoke with per-step receipts)
 */
export async function resumeReleaseFlowIfConfigured(
  input: ResumeReleaseFlowInput,
): Promise<ReleaseFlowResumeOutcome> {
  const release = input.config.release;
  if (!release) return { kind: "skipped_no_release_config" };
  // D14: a deploy nobody can verify never runs — refused before G3 is asked.
  if (release.deploy_recipe !== undefined && release.verify_recipe === undefined) {
    return { kind: "release_flow_blocked", reason: DEPLOY_WITHOUT_VERIFY_REASON };
  }
  const deployConfigured = release.deploy_recipe !== undefined;

  // Review L1 + Luke 30/09: a legacy receipt or an unknown deploy stops the run
  // BEFORE any G3 is withdrawn or asked again (code review P3 M1).
  const readCandidateSha = candidateShaReader(input);
  const deployStateStop = await precheckDeployStateBeforeRelease({
    ...input,
    release,
    readCandidateSha,
  });
  if (deployStateStop !== undefined) return deployStateStop;

  // CXR-03: a G3 bound to another candidate is withdrawn ⇒ the pre-G3 path below.
  // CXR-R2: so is one for this candidate whose current destination has no push yet.
  const { g3, withdrawalNote, concurrentWrite } = await readG3WithdrawingOtherCandidate({
    ...input,
    release,
    destination: pushDestinationFor(release, input.remoteEvidenceInput.sourceBranch),
    readCandidateSha,
  });
  if (concurrentWrite) {
    const reason = G3_CONCURRENT_WRITE_REASON;
    return { kind: "waiting_gate_g3", g3RequestId: g3.request_id ?? "", deployConfigured, reason };
  }
  if (g3.status === "approved" || g3.status === "pending") {
    const { candidateSha, baseSha } = await input.readCandidateAndBaseSha();
    const releaseManifestHash = computeReleaseManifestHash(candidateSha, release);
    const reRequested = await reRequestG3IfStale({
      controlRoot: input.controlRoot,
      g3,
      candidateSha,
      releaseManifestHash,
    });
    if (reRequested !== undefined) {
      return {
        kind: "waiting_gate_g3",
        g3RequestId: reRequested.requestId,
        deployConfigured,
        reason: reRequested.reason,
      };
    }
    if (g3.status === "pending") {
      return { kind: "waiting_gate_g3", g3RequestId: g3.request_id ?? "", deployConfigured };
    }
    return runApprovedG3ReleaseLeg({
      store: input.store,
      controlRoot: input.controlRoot,
      repoDir: input.repoDir,
      release,
      candidateSha,
      releaseManifestHash,
      g3RequestId: g3.request_id ?? "",
      approvedArtifactHash: g3.artifact_hash,
      verifyCi:
        input.verifyCiBeforeDeploy ??
        (() =>
          verifyCiOfApprovedCandidate({
            state: input.store.state,
            candidateSha,
            baseSha,
            destination: pushDestinationFor(release, input.remoteEvidenceInput.sourceBranch),
            remoteEvidenceInput: input.remoteEvidenceInput,
            remoteEvidenceDeps: input.remoteEvidenceDeps,
          })),
    });
  }

  // not_requested / waived / invalidated (a re-request after invalidation is
  // the SAME "start of REVIEWING" path — invalidateGateOnShaChange already
  // reset it to a fresh not_requested-shaped state upstream).
  return withG3WithdrawalNote(await resumeUpToG3(input, release, deployConfigured), withdrawalNote);
}

/**
 * Thin wrapper so supervisor-mission-run.ts's call site is one line — only
 * calls the factory + resumes when the ticket loop actually reached "done"
 * AND a release config exists; otherwise a no-op.
 */
export async function maybeResumeReleaseFlowAfterDone(input: {
  ticketLoopOutcome: string;
  config: AutopilotConfig;
  store: MissionStore;
  controlRoot: string;
  missionId: string;
  repoDir: string;
  resumeInputFactory:
    | ((
        config: AutopilotConfig,
      ) => Promise<
        Omit<ResumeReleaseFlowInput, "store" | "controlRoot" | "missionId" | "repoDir" | "config">
      >)
    | undefined;
}): Promise<ReleaseFlowResumeOutcome | undefined> {
  if (input.ticketLoopOutcome !== "done") return undefined;
  if (!input.config.release) return undefined;
  if (!input.resumeInputFactory) return undefined;

  const resumeInputs = await input.resumeInputFactory(input.config);
  return resumeReleaseFlowIfConfigured({
    ...resumeInputs,
    store: input.store,
    controlRoot: input.controlRoot,
    missionId: input.missionId,
    repoDir: input.repoDir,
    config: input.config,
  });
}

/** Re-export so `run-command-cli-wiring.ts` needs only ONE import for "release flow resume". */
export { approveG3Gate as approveG3ForReleaseFlow };
